/**
 * Direction context for RTL/LTR support.
 * Provides the current text direction based on the selected language.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import { getLanguageDirection } from '../../i18n';

type Direction = 'ltr' | 'rtl';

interface DirectionContextValue {
  direction: Direction;
  isRtl: boolean;
}

const DirectionContext = createContext<DirectionContextValue | undefined>(undefined);

interface DirectionProviderProps {
  children: React.ReactNode;
}

export function DirectionProvider({ children }: DirectionProviderProps) {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState<Direction>(() => {
    // Initialize from current language
    return getLanguageDirection(i18n.language);
  });

  // Update direction when language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newDirection = getLanguageDirection(lng);
      setDirection(newDirection);
      // Also update document direction
      document.documentElement.dir = newDirection;
      document.documentElement.lang = lng;
    };

    // Set initial direction
    handleLanguageChange(i18n.language);

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const value = useMemo<DirectionContextValue>(() => ({
    direction,
    isRtl: direction === 'rtl',
  }), [direction]);

  return (
    <DirectionContext.Provider value={value}>
      {/*
        Radix primitives read direction from their OWN context
        (@radix-ui/react-direction), not the DOM `dir` attribute. Without this
        provider, useDirection() falls back to 'ltr', so portalled surfaces
        (DropdownMenu, Popover, Select, Tooltip, ContextMenu) are stamped with
        dir="ltr" and resolve their ps-/pe-/start-/end-/align logic LTR even
        under <html dir="rtl">. Mounting it here makes every Radix surface
        direction-aware app-wide from one place.
      */}
      <RadixDirectionProvider dir={direction}>
        {children}
      </RadixDirectionProvider>
    </DirectionContext.Provider>
  );
}

export function useDirection(): DirectionContextValue {
  const context = useContext(DirectionContext);
  if (!context) {
    throw new Error('useDirection must be used within a DirectionProvider');
  }
  return context;
}

/**
 * Hook for components that need direction but may be used outside the provider.
 * Falls back to document direction or 'ltr' if not in provider context.
 */
export function useDirectionSafe(): DirectionContextValue {
  const context = useContext(DirectionContext);
  if (context) {
    return context;
  }
  // Fallback: read from document
  const docDir = (document.documentElement.dir as Direction) || 'ltr';
  return {
    direction: docDir,
    isRtl: docDir === 'rtl',
  };
}
